import { msg } from "../../locales/translate";
import { useLocale } from "../../hooks/useLocale";
import { DateTimeTooltip } from "src/view/conponents/DateTimeTooltip";
import { formatDisplayDateTime } from "src/utils/dateTime";
import { VBox } from "@ui/box/Box";
import { useCallback, useEffect, useRef, useState } from "react";
import { GD } from "src/GD";
import { Error } from "@ui/error/Error";
import { SectionTitle, SectionMeta } from "./styled";
import { PeriodSelector } from "./PeriodSelector";
import { statFilterSurface } from "./panelStyles";
import { IStatItem, IStatTable } from "./types";
import { KpiCard, type KpiKind } from "./KpiCard";
import { KpiMultipleCard } from "./KpiMultipleCard";
import { DonutCard } from "./DonutCard";
import { HBarChartCard } from "./HBarChartCard";
import { HBarChartSectionalCard } from "./HBarChartSectionalCard";
import { PieChartCard } from "./PieChartCard";
import { AreaChartCard } from "./AreaChartCard";
import { LineChartCard } from "./LineChartCard";
import { useStatChannels, ALL_CHANNELS_VALUE } from "./useStatChannels";
import { useStatClients } from "./useStatClients";
import { AUTH_STATE } from "src/services/AuthService";
import { OverviewAnalysisCard } from "./OverviewAnalysisCard";
import { OverviewAnomaliesCard } from "./OverviewAnomaliesCard";
import { SessionsRateCard } from "./SessionsRateCard";
import { SharedEmailsCard } from "./SharedEmailsCard";
import { canViewSharedEmails } from "./sharedEmails";
import { OverviewAnalysisFilters, OverviewPeriodKind } from "./useOverviewAnalysis";
import { loadOverview } from "./overviewData";
import { ChartTransactionsPopup } from "./ChartTransactionsPopup";
import { getChartBucket, type ChartBucket, type ChartTransactionScope } from "./chartTransactions";
import { useTranslation } from "react-i18next";
import { BankAnalysisPopup } from "./BankAnalysisPopup";
import { getRankingAnalysisEntity, type BankAnalysisRequest } from "./overviewBankAnalysis";
import type { SectionalBarListProps } from "./SectionalBarList";
import { S_BAR_CHART_DETAILS, S_BAR_CHART_SECTIONAL_DETAILS } from "./signals";
import { finishClientDashboardComparison, getClientDashboardSelection, registerClientDashboard } from "./clientDashboardComparison";

import { isStatisticsSummaryOnly, OVERVIEW_ANALYSIS_USER_IDS } from "src/permissions";
import { selectSharedPreset, selectSharedRange, useSharedPeriod } from "./sharedPeriod";
const PERIOD_KINDS: OverviewPeriodKind[] = ["today", "yesterday", "last_7_days", "last_30_days", "this_month", "last_month"];

/** The nearest ancestor that scrolls by itself, such as a popup tab; on pages the window scrolls and there is none. */
const findScrollParent = (node: HTMLElement): HTMLElement | null => {
	for (let element = node.parentElement; element; element = element.parentElement) {
		const overflow = getComputedStyle(element).overflowY;
		if (overflow === "auto" || overflow === "scroll" || overflow === "overlay") return element;
	}
	return null;
};

export const StatView = ({
	client_id,
	type,
	size = 3,
	preserveClientComparison = false,
}: {
	client_id: number;
	type: "client" | "merchant" | "project";
	size?: number;
	preserveClientComparison?: boolean;
}) => {
	useLocale();
	const { t, i18n } = useTranslation();
	const dates = [
		t("dashboard.periods.today"),
		t("dashboard.periods.yesterday"),
		t("dashboard.periods.last7Days"),
		t("dashboard.periods.last30Days"),
		t("dashboard.periods.thisMonth"),
		t("dashboard.periods.lastMonth"),
	];
	const [project] = GD.B_SELECTED_PROJECT.use();
	const authState = GD.S_AUTH_STATUS.use([]);
	const [user] = GD.REQ_PROFILE.useValue(undefined, undefined, [project?.id, authState]);
	const summaryOnly = isStatisticsSummaryOnly(user?.email);
	const canAnalyze = !summaryOnly && authState === AUTH_STATE.AUTHORIZED && !!user && OVERVIEW_ANALYSIS_USER_IDS.includes(user.id) && !!project;
	const comparisonScope = preserveClientComparison && type === "client" && project && user && authState === AUTH_STATE.AUTHORIZED
		? `${project.id}:${user.id}` : undefined;
	// The period is shared with the other pickers for the session; the client comparison hand-off keeps only the currency.
	const { selectedDate, manualRange } = useSharedPeriod();
	const [selectedCurrency, setSelectedCurrency] = useState<string>(() => comparisonScope ? getClientDashboardSelection(comparisonScope, client_id)?.currency ?? "EUR" : "EUR");
	useEffect(() => {
		if (!comparisonScope) return;
		return registerClientDashboard({ scope: comparisonScope, clientId: client_id,
			selection: { selectedDate, manualRange, currency: selectedCurrency } });
	}, [comparisonScope, client_id, selectedDate, manualRange, selectedCurrency]);
	const targetKey = `${project?.id}:${type}:${client_id}`;
	const [channelSelection, setChannelSelection] = useState({ targetKey, value: ALL_CHANNELS_VALUE });
	const selectedChannel = channelSelection.targetKey === targetKey ? channelSelection.value : ALL_CHANNELS_VALUE;
	const { options: channelOptions, busy: channelsBusy } = useStatChannels(client_id, type);
	// Terminal and client filters reset when the dashboard target changes, like the channel filter.
	const [scopeSelection, setScopeSelection] = useState<{ targetKey: string; instruments: string[]; clients: string[] }>({ targetKey, instruments: [], clients: [] });
	const scope = scopeSelection.targetKey === targetKey ? scopeSelection : { targetKey, instruments: [], clients: [] };
	const updateScope = (patch: Partial<Pick<typeof scope, "instruments" | "clients">>) => setScopeSelection({ ...scope, ...patch });
	const clientFilterEnabled = type === "project" && !summaryOnly;
	const { options: clientOptions, busy: clientsBusy, failed: clientsFailed } = useStatClients(clientFilterEnabled, project?.id);
	const [busy, setBusy] = useState<boolean>(false);
	const [statData, setStatData] = useState<IStatTable[] | null>(null);
	const visibleStatData = summaryOnly && Array.isArray(statData)
		? statData.map(table => ({
			...table,
			items: table.items.filter(item => item && (
				item.type === "donutChart" && (item.title === "Conversion" || item.title === "Overall conversion")
				|| item.type === "informerMultiple" && item.title === "Transactions"
				|| item.type === "informer" && /^(Turnover|Declines|Refunds|Chargebacks)(, [A-Z]{3})?$/.test(item.title)
			)),
		})).filter(table => table.items.length > 0)
		: statData;
	const [statTargetKey, setStatTargetKey] = useState(targetKey);
	const periodSelectorSentinelRef = useRef<HTMLDivElement>(null);
	const [headerHeight] = GD.B_HEADER_HEIGHT.use(undefined, undefined, []);
	const [periodSelectorStuck, setPeriodSelectorStuck] = useState(false);
	const [panelScroller, setPanelScroller] = useState<{ padding: number } | null>(null);
	const [error, setError] = useState<IError>();
	const [loadedStatKey, setLoadedStatKey] = useState("");
	const [chartSelection, setChartSelection] = useState<{ key: string; scope: ChartTransactionScope; bucket: ChartBucket | null } | null>(null);
	const closeChartTransactions = useCallback(() => setChartSelection(null), []);
	const [analysisSelection, setAnalysisSelection] = useState<{ key: string; request: BankAnalysisRequest } | null>(null);
	const closeRankingAnalysis = useCallback(() => setAnalysisSelection(null), []);
	const localizeStatItem = (item: IStatItem): IStatItem => {
		const titleMatch = /^(Turnover|Declines), ([A-Z]{3})$/.exec(item.title);
		const titleKeys: Record<string, string> = {
			OVERVIEW: "stats.titles.overview",
			Overview: "stats.titles.overview",
			"Top statistics": "stats.titles.topStatistics",
			Conversion: "stats.titles.conversion",
			Transactions: "stats.titles.transactions",
			Refunds: "stats.titles.refunds",
			Chargebacks: "stats.titles.chargebacks",
			"Top channels": "stats.titles.topChannels",
			"Top countries": "stats.titles.topCountries",
			"Top banks": "stats.titles.topBanks",
			"Top errors": "stats.titles.topErrors",
			"Top statuses": "stats.titles.topStatuses",
		};
		const subtitleKeys: Record<string, string> = {
			"Total turnover amount": "stats.subtitles.turnover",
			"Total fails amount": "stats.subtitles.declines",
			"Total failed amount": "stats.subtitles.declines",
			"Total refunds amount": "stats.subtitles.refunds",
			"Total chargebacks amount": "stats.subtitles.chargebacks",
		};
		const title = titleMatch
			? `${t(titleMatch[1] === "Turnover" ? "stats.titles.turnover" : "stats.titles.declines")}, ${titleMatch[2]}`
			: titleKeys[item.title] ? t(titleKeys[item.title]) : item.title;
		return {
			...item,
			title,
			subtitle: item.subtitle && subtitleKeys[item.subtitle] ? t(subtitleKeys[item.subtitle]) : item.subtitle,
			labels: item.type === "informerMultiple" && item.title === "Transactions"
				? (item.labels ?? ["", ""]).map((label, index) => {
					if (label === "Success" || !label && index === 0) return t("stats.labels.success");
					if (label === "Fail" || !label && index === 1) return t("stats.labels.fail");
					return label;
				})
				: item.labels,
		};
	};

	useEffect(() => {
		const sentinel = periodSelectorSentinelRef.current;
		if (!sentinel) return;
		const scroller = findScrollParent(sentinel);
		const padding = scroller ? parseFloat(getComputedStyle(scroller).paddingTop) || 0 : 0;
		setPanelScroller(scroller ? { padding } : null);

		const updateStuckState = () => {
			// Inside a scrolling panel the filter pins to the panel's top edge, otherwise under the page header.
			const edge = scroller ? scroller.getBoundingClientRect().top : headerHeight ?? 0;
			setPeriodSelectorStuck(sentinel.getBoundingClientRect().top < edge - 0.5);
		};

		updateStuckState();
		window.addEventListener("scroll", updateStuckState, true);
		window.addEventListener("resize", updateStuckState);

		return () => {
			window.removeEventListener("scroll", updateStuckState, true);
			window.removeEventListener("resize", updateStuckState);
		};
	}, [headerHeight]);

	const fmt = (d: Date) => {
		const y = d.getFullYear();
		const m = String(d.getMonth() + 1).padStart(2, "0");
		const day = String(d.getDate()).padStart(2, "0");
		return `${y}-${m}-${day}`;
	};

	const getFrom = (index: number) => {
		const now = new Date();
		switch (index) {
			case 0:
				return fmt(now);
			case 1: {
				const d = new Date(now);
				d.setDate(d.getDate() - 1);
				return fmt(d);
			}
			case 2: {
				const d = new Date(now);
				d.setDate(d.getDate() - 6);
				return fmt(d);
			}
			case 3: {
				const d = new Date(now);
				d.setDate(d.getDate() - 29);
				return fmt(d);
			}
			case 4:
				return fmt(new Date(now.getFullYear(), now.getMonth(), 1));
			case 5:
				return fmt(new Date(now.getFullYear(), now.getMonth() - 1, 1));
			default:
				return fmt(now);
		}
	};

	const getTo = (index: number) => {
		const now = new Date();
		switch (index) {
			case 0:
				return fmt(now);
			case 1: {
				const d = new Date(now);
				d.setDate(d.getDate() - 1);
				return fmt(d);
			}
			case 2:
			case 3:
			case 4:
				return fmt(now);
			case 5:
				return fmt(new Date(now.getFullYear(), now.getMonth(), 0));
			default:
				return fmt(now);
		}
	};

	const from = manualRange?.from ?? getFrom(selectedDate);
	const to = manualRange?.to ?? getTo(selectedDate);
	const periodKind: OverviewPeriodKind = manualRange ? "custom" : PERIOD_KINDS[selectedDate];
	const channelId = selectedChannel && selectedChannel !== ALL_CHANNELS_VALUE ? Number(selectedChannel) : undefined;
	// Sorted so the same selection in a different click order hits the same cache entry.
	const instruments = [...scope.instruments].sort();
	const clientIds = clientFilterEnabled ? scope.clients.map(Number).filter(id => id > 0).sort((a, b) => a - b) : [];
	const scopeFilters = {
		...(instruments.length ? { instruments } : {}),
		...(clientIds.length ? { client_ids: clientIds } : {}),
	};
	// Drill-down popups query their own endpoints without these filters, so they stay off while one is set.
	const scopeFiltered = instruments.length > 0 || clientIds.length > 0;
	const statKey = JSON.stringify([targetKey, user?.id, authState, from, to, selectedCurrency, channelId, instruments, clientIds, size]);
	const stale = loadedStatKey !== statKey;
	const analysisFilters: OverviewAnalysisFilters = {
		target_id: client_id,
		target_type: type,
		from,
		to,
		currency: selectedCurrency,
		...(channelId !== undefined ? { channel_id: channelId } : {}),
		...scopeFilters,
		period_kind: periodKind,
		size: size ?? 3,
	};

	useEffect(() => {
		let cancelled = false;
		if (!user || !project || authState !== AUTH_STATE.AUTHORIZED) return;
		const fetchStat = async () => {
			setError(undefined);
			setBusy(true);
			try {
				const data = await loadOverview({
						target_id: client_id,
						target_type: type,
						from,
						to,
						currency: selectedCurrency,
						size: size ?? 3,
						...(channelId !== undefined ? { channel_id: channelId } : {}),
						...scopeFilters,
					}, user.id, project.id);
				if (cancelled) return;
				setStatData(data);
				setStatTargetKey(targetKey);
				setLoadedStatKey(statKey);
			} catch (failure) {
				if (!cancelled) {
					setStatData(null);
					setError(failure && typeof failure === "object" && "code" in failure && "message" in failure
						? failure as IError
						: { code: -1, message: msg("ui_acbb777bc4") });
				}
			} finally {
				if (!cancelled) setBusy(false);
			}
		};
		void fetchStat();
		return () => { cancelled = true; };
	// statKey carries the instrument/client selection; scopeFilters is rebuilt every render.
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [client_id, type, project, user, authState, targetKey, from, to, selectedCurrency, channelId, size, statKey]);

	useEffect(() => { setChartSelection(null); setAnalysisSelection(null); }, [statKey]);
	useEffect(() => {
		if (comparisonScope && !busy && (loadedStatKey === statKey || error)) finishClientDashboardComparison(comparisonScope, client_id);
	}, [comparisonScope, client_id, busy, loadedStatKey, statKey, error]);
	useEffect(() => {
		const closeRankingReports = () => {
			S_BAR_CHART_DETAILS.invoke(null);
			S_BAR_CHART_SECTIONAL_DETAILS.invoke(null);
		};
		closeRankingReports();
		return closeRankingReports;
	}, [statKey, busy, canAnalyze]);

	const getRankingRowAction = (item: IStatItem): SectionalBarListProps["getRowAction"] => {
		if (!canAnalyze || !user || !project || busy || stale || channelId !== undefined || scopeFiltered) return undefined;
		return label => {
			const bank = getRankingAnalysisEntity(item.title, label, i18n.language);
			if (!bank) return undefined;
			const labelKey = bank.type === "country" ? "analysis.openAnalysisCountry" : bank.type === "email" ? "analysis.openAnalysisEmail" : "analysis.openAnalysisBank";
			return {
				label: t(labelKey, { name: bank.label }),
				onClick: () => setAnalysisSelection({ key: statKey, request: { bank, from, to, currency: selectedCurrency, userId: user.id, projectId: project.id } }),
			};
		};
	};

	const getBlockSpan = (type: IStatItem["type"]): { col: number; row: number } => {
		switch (type) {
			case "informer":
				return { col: 1, row: 1 };
			case "donutChart":
			case "informerMultiple":
				return { col: 1, row: size === 1 ? 1 : 2 };
			default:
				return { col: 1, row: 1 };
		}
	};

	const getBlockByType = (item: IStatTable["items"][number]) => {
		if (!item) return null;
		const displayItem = localizeStatItem(item);
		switch (item.type) {
			case "header":
				return (
						<SectionTitle>
							<h2>{displayItem.title}</h2>
							{item.data && typeof item.data === "object" && "collected" in item.data && (
								<SectionMeta><DateTimeTooltip value={item.data.collected as any} locale={i18n.language}>{t("dashboard.collectedAt", { date: formatDisplayDateTime(item.data.collected as any, { locale: i18n.language }) })}</DateTimeTooltip></SectionMeta>
							)}
							{item.data && typeof item.data === "object" && "generationTime" in item.data && (
								<SectionMeta>{t("dashboard.generatedIn", { milliseconds: (item.data as any).generationTime })}</SectionMeta>
							)}
						</SectionTitle>
				);
			case "informer": {
				const kinds: Record<string, KpiKind> = { Turnover: "turnover", Declines: "declines", Refunds: "refunds", Chargebacks: "chargebacks" };
				return <KpiCard item={displayItem} kind={kinds[item.title.split(", ")[0]]} />;
			}
			case "informerMultiple":
				return <KpiMultipleCard item={displayItem} isTransactions={item.title === "Transactions"} />;
			case "donutChart":
				return <DonutCard item={item.title === "Conversion" ? { ...displayItem, title: t("stats.titles.overallConversion"), subtitle: t("stats.summary.conversionHint") } : displayItem} preferenceKey={`statistics-conversion:${user?.id}:${item.title}`} />;
			case "hBarChart":
				return <HBarChartCard item={displayItem} />;
			case "hBarChartSections":
				return <HBarChartSectionalCard item={displayItem} getRowAction={getRankingRowAction(item)} />;
			case "pieChart":
				return <PieChartCard item={displayItem} preferenceKey={`statistics-distribution:${user?.id}:${item.title}`} />;
			case "vBarChart":
				return <AreaChartCard item={displayItem} />;
			case "lineChart":
				return <LineChartCard item={displayItem} preferenceKey={`statistics-chart:${user?.id}:${item.title}`} showTotal={item.title === "Transactions"} onPointClick={item.title === "Transactions" && !scopeFiltered && !busy && loadedStatKey === statKey && user && project && authState === AUTH_STATE.AUTHORIZED ? index => {
					setChartSelection({
						key: statKey,
						scope: { target_id: client_id, target_type: type, from, to, currency: selectedCurrency, channel_id: channelId, userId: user.id, projectId: project.id },
						bucket: getChartBucket(item.data, index, from, to),
					});
				} : undefined} />;
			default:
				return null;
		}
	};

	return (
		<VBox sx={{ minHeight: "0px", flexGrow: 1, gap: "16px" }}>
			{!summaryOnly && chartSelection && chartSelection.key === statKey && <ChartTransactionsPopup scope={chartSelection.scope} bucket={chartSelection.bucket} onClose={closeChartTransactions} />}
			{canAnalyze && analysisSelection?.key === statKey && <BankAnalysisPopup request={analysisSelection.request} onClose={closeRankingAnalysis}
				onEntity={bank => setAnalysisSelection(selection => selection?.key === statKey ? { ...selection, request: { ...selection.request, bank } } : null)} />}
			{error && <Error error={error} />}

			{user && project && authState === AUTH_STATE.AUTHORIZED && (
				<SessionsRateCard key={`sessions-rate:${targetKey}:${user.id}`} targetId={client_id} targetType={type} projectId={project.id} userId={user.id} />
			)}
			{!summaryOnly && user && project && authState === AUTH_STATE.AUTHORIZED && (
				<OverviewAnomaliesCard key={`daily-traffic:${targetKey}:${user.id}`} scope={{ targetId: client_id, targetType: type, projectId: project.id, userId: user.id }} />
			)}

			{/* Keep the zero-height sentinel outside the gap stack, and the sticky filter inside a full-height parent. */}
			<div style={{ minWidth: 0 }}>
				<div ref={periodSelectorSentinelRef} aria-hidden style={{ height: 0, flexShrink: 0 }} />
				<VBox sx={{ gap: "16px", minWidth: 0 }}>
					<PeriodSelector
						dates={dates}
						selectedDate={manualRange ? -1 : selectedDate}
						onDateChange={selectSharedPreset}
						from={from}
						to={to}
						onRangeChange={selectSharedRange}
						selectedCurrency={selectedCurrency}
						hideFilters
						onCurrencyChange={setSelectedCurrency}
						channels={channelOptions}
						channelsBusy={channelsBusy}
						selectedChannel={selectedChannel}
						onChannelChange={value => setChannelSelection({ targetKey, value })}
						selectedInstruments={scope.instruments}
						onInstrumentsChange={summaryOnly ? undefined : values => updateScope({ instruments: values })}
						clients={clientOptions}
						clientsBusy={clientsBusy}
						clientsFailed={clientsFailed}
						selectedClients={scope.clients}
						onClientsChange={clientFilterEnabled ? values => updateScope({ clients: values }) : undefined}
						size={size}
						style={{
							position: "sticky",
							// A sticky box stops at the scroller's padding edge; the negative inset lets it meet the panel's top.
							top: panelScroller ? -panelScroller.padding : headerHeight ?? 0,
							zIndex: 10,
							...statFilterSurface,
							borderRadius: periodSelectorStuck ? 0 : 12,
							boxShadow: periodSelectorStuck
								? "0 8px 18px rgba(26, 29, 38, 0.08)"
								: "none",
						}}
					/>

					{canAnalyze && user && project && (
						<>
							{channelId === undefined ? (
								<OverviewAnalysisCard filters={analysisFilters} userId={user.id} projectId={project.id} />
							) : (
								<div role="status" style={{ padding: "12px 16px", borderRadius: 12, border: "1px solid #dbe1e9", borderLeft: "4px solid #667085", background: "#f6f8fb", color: "#566174", fontSize: 14, lineHeight: 1.5 }}>
									<strong>{t("dashboard.allChannelsSummary")}</strong> {t("dashboard.allChannelsHint")}
								</div>
							)}
						</>
					)}

					{user && project && authState === AUTH_STATE.AUTHORIZED && type === "project" && canViewSharedEmails(user, project.id) && (
						<SharedEmailsCard key={`shared-emails:${targetKey}:${user.id}`} scope={{ userId: user.id, projectId: project.id,
							filters: { from, to, currency: selectedCurrency } }} />
					)}

					{/* Data tables */}
					{preserveClientComparison && stale && !error && <div role="status" style={{ position: "fixed", top: (headerHeight ?? 60) + 8, right: 24, zIndex: 30, padding: "8px 14px", background: "#fff", border: "1px solid #dbe1e9", borderRadius: 8 }}>{t("common.loading")}</div>}
					{visibleStatData && (statTargetKey === targetKey || preserveClientComparison) && (
						<div aria-busy={busy || stale} ref={node => { if (node) node.inert = busy || stale; }} style={{ display: "flex", flexDirection: "column", gap: 24, opacity: busy || stale ? 0.35 : 1, transition: "opacity 0.3s" }}>
							{Array.isArray(visibleStatData) &&
								visibleStatData.map((table, index) => (
									<div key={index} style={{ display: "flex", flexDirection: "column", gap: 24, minWidth: 0 }}>
										{!summaryOnly && (table.title || table.subtitle) && <div style={{ display: "grid", gap: 4 }}>
											{table.title && <h2 style={{ fontSize: 16, lineHeight: "24px", fontWeight: 600, color: "#344054", margin: 0 }}>{table.title}</h2>}
											{table.subtitle && <p style={{ fontSize: 13, lineHeight: "20px", color: "#667085", margin: 0 }}>{table.subtitle}</p>}
										</div>}
										{(() => {
											const groups: { header: IStatItem | null; items: IStatItem[] }[] = [];
											for (const item of table.items) {
												if (item && item.type === "header") {
													groups.push({ header: item, items: [] });
												} else if (item) {
													if (groups.length === 0) groups.push({ header: null, items: [] });
													groups[groups.length - 1].items.push(item);
												}
											}
											return groups.map((group, gi) => (
												<div key={`${index}-g${gi}`} style={{ display: "flex", flexDirection: "column", gap: 12, minWidth: 0 }}>
													{group.header && !(group.items.length === 1 && group.header.title === group.items[0].title && !group.header.data) && getBlockByType(group.header)}
													<div
														style={{
															display: "grid",
															gridTemplateColumns: `repeat(${size}, minmax(0, 1fr))`,
															gridAutoFlow: "row dense",
															gap: 12,
														}}>
														{(() => {
															const lineChartCount = group.items.filter(i => i && i.type === "lineChart").length;
															return group.items.map((item, ii) => {
																const span = getBlockSpan(item?.type);
																const colSpan = item && item.type === "lineChart" && lineChartCount === 1 ? size : span.col;
																return (
																	<div
																		style={{
																			gridColumn: `auto / span ${colSpan}`,
																			gridRow: span.row > 1 ? `auto / span ${span.row}` : undefined,
																			height: "100%",
																		}}
																		key={`${index}-g${gi}-${ii}`}>
																		{getBlockByType(item)}
																	</div>
																);
															});
														})()}
													</div>
												</div>
											));
										})()}
									</div>
								))}
						</div>
					)}
				</VBox>
			</div>
		</VBox>
	);
};
